import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Typography, Button, Space, Form, Input, InputNumber,
    DatePicker, Select, Table, Divider, message, Switch, Spin
} from "antd";
import { PlusOutlined, DeleteOutlined, SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import dayjs from "dayjs";
import axiosInstance from "../../../configs";

const { Title, Text } = Typography;

export default function GeneralEdit() {
    const history = useHistory();
    const { id } = useParams();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [initLoading, setInitLoading] = useState(true);
    const [contacts, setContacts] = useState([]);
    const [items, setItems] = useState([]);

    // Tax States
    const [ppnEnabled, setPpnEnabled] = useState(true);
    const [pphEnabled, setPphEnabled] = useState(false);
    const [ppnRate, setPpnRate] = useState(11);
    const [pphRate, setPphRate] = useState(2);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        setInitLoading(true);
        try {
            // Fetch contacts
            let contactsData = [];
            try {
                const resContacts = await axiosInstance.get("/finance/contacts");
                contactsData = resContacts.data.data || [];
            } catch (e) { /* empty */ }
            setContacts(contactsData);

            // Fetch invoice data
            if (id) {
                const resInv = await axiosInstance.get(`/finance/sales-invoices/${id}`);
                const inv = resInv.data.data;
                if (inv) {
                    form.setFieldsValue({
                        idCustomer: inv.id_customer ? String(inv.id_customer) : undefined,
                        invoiceNumber: inv.invoice_number,
                        invoiceDate: inv.invoice_date ? dayjs(inv.invoice_date) : dayjs(),
                        dueDate: inv.due_date ? dayjs(inv.due_date) : dayjs().add(7, 'day'),
                    });

                    // Load line items from invoice details
                    const details = inv.details || inv.items || [];
                    if (details.length > 0) {
                        setItems(details.map((d, i) => ({
                            key: Date.now() + i,
                            name: d.name || d.service_name || d.description || '',
                            qty: d.qty || d.quantity || 1,
                            price: d.price || d.unit_price || 0,
                            total: (d.qty || d.quantity || 1) * (d.price || d.unit_price || 0),
                        })));
                    } else {
                        setItems([{ key: Date.now(), name: '', qty: 1, price: 0, total: 0 }]);
                    }

                    // Restore tax settings
                    if (inv.ppn_rate !== undefined && inv.ppn_rate !== null) {
                        setPpnRate(inv.ppn_rate);
                        setPpnEnabled(inv.ppn_rate > 0);
                    }
                    if (inv.pph_rate !== undefined && inv.pph_rate !== null) {
                        setPphRate(inv.pph_rate);
                        setPphEnabled(inv.pph_rate > 0);
                    }
                }
            }
        } catch (err) {
            console.error(err);
            message.error("Gagal memuat data invoice");
        } finally {
            setInitLoading(false);
        }
    };

    const updateItem = (key, field, value) => {
        const newItems = items.map(item => {
            if (item.key === key) {
                const newItem = { ...item, [field]: value };
                newItem.total = newItem.qty * newItem.price;
                return newItem;
            }
            return item;
        });
        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, { key: Date.now(), name: '', qty: 1, price: 0, total: 0 }]);
    };

    const removeItem = (key) => {
        if (items.length > 1) {
            setItems(items.filter(i => i.key !== key));
        }
    };

    const dppAmount = items.reduce((acc, curr) => acc + curr.total, 0);
    const ppnAmount = ppnEnabled ? (dppAmount * ppnRate) / 100 : 0;
    const pphAmount = pphEnabled ? (dppAmount * pphRate) / 100 : 0;
    const totalAmountNet = dppAmount + ppnAmount - pphAmount;

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const payload = {
                idCustomer: values.idCustomer,
                invoiceNumber: values.invoiceNumber,
                invoiceDate: values.invoiceDate.format("YYYY-MM-DD"),
                dueDate: values.dueDate.format("YYYY-MM-DD"),
                type: 4, // General
                items: items.map(i => ({ name: i.name, qty: i.qty, price: i.price, total: i.total })),
                dppAmount,
                ppnRate: ppnEnabled ? ppnRate : 0,
                ppnAmount,
                pphRate: pphEnabled ? pphRate : 0,
                pphAmount,
                totalAmountNet
            };

            await axiosInstance.put(`/finance/sales-invoices/${id}`, payload);
            message.success("Invoice berhasil diupdate!");
            history.push("/finance/sales");
        } catch (err) {
            console.error(err);
            message.error("Gagal mengupdate invoice: " + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Deskripsi Layanan',
            dataIndex: 'name',
            render: (_, record) => (
                <Input
                    value={record.name}
                    placeholder="Nama Layanan"
                    onChange={(e) => updateItem(record.key, 'name', e.target.value)}
                />
            )
        },
        {
            title: 'Qty',
            dataIndex: 'qty',
            width: 100,
            render: (_, record) => (
                <InputNumber
                    min={1}
                    value={record.qty}
                    onChange={(v) => updateItem(record.key, 'qty', v)}
                />
            )
        },
        {
            title: 'Harga Satuan',
            dataIndex: 'price',
            width: 200,
            render: (_, record) => (
                <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\s?|(,*)/g, '')}
                    value={record.price}
                    onChange={(v) => updateItem(record.key, 'price', v)}
                />
            )
        },
        {
            title: 'Total',
            dataIndex: 'total',
            width: 200,
            align: 'right',
            render: (val) => <Text strong>{(val || 0).toLocaleString('id-ID')}</Text>
        },
        {
            title: '',
            width: 50,
            render: (_, record) => (
                <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeItem(record.key)}
                />
            )
        }
    ];

    if (initLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', flexDirection: 'column', gap: '16px' }}>
                <Spin size="large" />
                <Text>Memuat data invoice...</Text>
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Button icon={<ArrowLeftOutlined />} onClick={() => history.goBack()}>Kembali</Button>

                <Card title={<Title level={4} style={{ margin: 0 }}>Edit Invoice Umum</Title>}>
                    <Form form={form} layout="vertical">
                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item name="idCustomer" label="Ditujukan Untuk (Customer)" rules={[{ required: true }]}>
                                    <Select
                                        showSearch
                                        placeholder="Pilih Customer"
                                        optionFilterProp="children"
                                    >
                                        {contacts.map(c => (
                                            <Select.Option key={c.idContact} value={String(c.idContact)}>{c.name}</Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="invoiceNumber" label="Nomor Invoice" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item name="invoiceDate" label="Tanggal Invoice" rules={[{ required: true }]}>
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="dueDate" label="Jatuh Tempo" rules={[{ required: true }]}>
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left">Detail Tagihan</Divider>

                        <Table
                            dataSource={items}
                            columns={columns}
                            pagination={false}
                            rowKey="key"
                            footer={() => (
                                <Button type="dashed" onClick={addItem} block icon={<PlusOutlined />}>
                                    Tambah Baris
                                </Button>
                            )}
                        />

                        <Row justify="end" style={{ marginTop: 24 }}>
                            <Col xs={24} md={10}>
                                <Card size="small" style={{ background: '#f9f9f9' }}>
                                    <Row justify="space-between" style={{ marginBottom: 8 }}>
                                        <Col>DPP (Total Harga):</Col>
                                        <Col><Text strong>Rp {dppAmount.toLocaleString()}</Text></Col>
                                    </Row>

                                    <Row justify="space-between" style={{ marginBottom: 8 }} align="middle">
                                        <Col>
                                            <Space>PPN ({ppnRate}%): <Switch size="small" checked={ppnEnabled} onChange={setPpnEnabled} /></Space>
                                        </Col>
                                        <Col><Text strong>Rp {ppnAmount.toLocaleString()}</Text></Col>
                                    </Row>

                                    <Row justify="space-between" style={{ marginBottom: 8 }} align="middle">
                                        <Col>
                                            <Space>PPh 23 ({pphRate}%): <Switch size="small" checked={pphEnabled} onChange={setPphEnabled} /></Space>
                                        </Col>
                                        <Col><Text strong style={{ color: 'red' }}>- Rp {pphAmount.toLocaleString()}</Text></Col>
                                    </Row>

                                    <Divider style={{ margin: '12px 0' }} />

                                    <Row justify="space-between">
                                        <Col><Title level={4}>TOTAL NET:</Title></Col>
                                        <Col><Title level={4} style={{ color: '#1890ff' }}>Rp {totalAmountNet.toLocaleString()}</Title></Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>

                        <Row justify="center" style={{ marginTop: 32 }}>
                            <Space>
                                <Button size="large" onClick={() => history.push('/finance/sales')}>
                                    Batal
                                </Button>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<SaveOutlined />}
                                    loading={loading}
                                    onClick={handleSubmit}
                                    style={{ width: 200, height: 50, borderRadius: 8 }}
                                >
                                    Simpan Perubahan
                                </Button>
                            </Space>
                        </Row>
                    </Form>
                </Card>
            </Space>
        </div>
    );
}
